import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableHead,
    TextField,
    Tooltip,
    Typography,
    alpha,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Popup from "../../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../../components/styles";
import { getApiBaseUrl } from "../../../config/api";
import {
    EmptyResultsRow,
    KpiCard,
    ResponsiveTableWrap,
    ResultsPageContainer,
    ResultsPageHeader,
    ResultsSectionPaper,
} from "../examRelated/resultsUi";
import {
    TermRecordsScopeAlert,
    TermRecordsScopeTabs,
} from "../../../components/term/TermRecordsScopeTabs";
import { useTermRecordsScope } from "../../../hooks/useTermRecordsScope";
import { buildTermRecordsQuery } from "../../../utils/termRecordsScope";

const STATUS_OPTIONS = ["All", "Submitted", "Draft", "Approved", "Rejected", "Returned"];

const STATUS_CHIP = {
    Draft: "default",
    Submitted: "info",
    Approved: "success",
    Rejected: "error",
    Returned: "warning",
};

const WEEK_LABELS = {
    1: "ONE",
    2: "TWO",
    3: "THREE",
    4: "FOUR",
    5: "FIVE",
    6: "SIX",
    7: "SEVEN",
    8: "EIGHT",
    9: "NINE",
    10: "TEN",
    11: "ELEVEN",
    12: "TWELVE",
    13: "THIRTEEN",
    14: "FOURTEEN",
};

function safeParse(value) {
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function schemeBlocks(scheme) {
    if (Array.isArray(scheme?.entries) && scheme.entries.length) return scheme.entries;
    if (Array.isArray(scheme?.weeks) && scheme.weeks.length) return scheme.weeks;
    return [];
}

function blockProgressLabel(scheme) {
    const blocks = schemeBlocks(scheme);
    const total = blocks.length;
    if (!total) return "No blocks";
    const taught = blocks.filter((b) => b.isTaught || b.isCovered).length;
    return `${taught} / ${total} taught`;
}

function blockProgressValue(scheme) {
    const blocks = schemeBlocks(scheme);
    if (!blocks.length) return 0;
    const taught = blocks.filter((b) => b.isTaught || b.isCovered).length;
    return Math.round((taught / blocks.length) * 100);
}

function weekRangeLabel(entry) {
    const from = WEEK_LABELS[entry.weekFrom] || entry.weekFrom || entry.weekNumber;
    const to = WEEK_LABELS[entry.weekTo] || entry.weekTo;
    if (from && to && from !== to) return `Week ${from} – ${to}`;
    if (from) return `Week ${from}`;
    if (entry.weekNumber) return `Week ${entry.weekNumber}`;
    return "Block";
}

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function BulletList({ text }) {
    const lines = String(text || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
    if (!lines.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                —
            </Typography>
        );
    }
    return (
        <Stack spacing={0.5}>
            {lines.map((line, i) => (
                <Typography key={i} variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    • {line.replace(/^[•-]\s*/, "")}
                </Typography>
            ))}
        </Stack>
    );
}

function LabelBlock({ label, children }) {
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}

function SchemeEntryReadOnly({ entry }) {
    const methods = safeParse(entry.teachingMethods);
    const resources = safeParse(entry.resources);
    const isLegacyWeek = entry.weekNumber != null && !entry.weekFrom;

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box
                sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                    borderBottom: 1,
                    borderColor: "divider",
                }}
            >
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={0.5}>
                    <Typography variant="subtitle2" fontWeight={800}>
                        {isLegacyWeek ? `Week ${entry.weekNumber}: ${entry.topic || "—"}` : weekRangeLabel(entry)}
                        {entry.topic && !isLegacyWeek ? ` · ${entry.topic}` : ""}
                    </Typography>
                    {(entry.isTaught || entry.isCovered) && (
                        <Chip size="small" label="Taught" color="success" variant="outlined" />
                    )}
                </Stack>
                {(entry.dateFrom || entry.dateTo) && (
                    <Typography variant="caption" color="text.secondary">
                        {formatDate(entry.dateFrom)}
                        {entry.dateTo ? ` – ${formatDate(entry.dateTo)}` : ""}
                    </Typography>
                )}
            </Box>

            <Box sx={{ p: 2 }}>
                {isLegacyWeek ? (
                    <Stack spacing={1}>
                        <LabelBlock label="Subtopics">
                            <Typography variant="body2">{entry.subtopics || "—"}</Typography>
                        </LabelBlock>
                        <LabelBlock label="Objectives">
                            <Typography variant="body2">{entry.objectives || "—"}</Typography>
                        </LabelBlock>
                        <LabelBlock label="Resources">
                            <Typography variant="body2">{entry.resources || "—"}</Typography>
                        </LabelBlock>
                        <LabelBlock label="Teaching method">
                            <Typography variant="body2">{entry.teachingMethod || "—"}</Typography>
                        </LabelBlock>
                        <LabelBlock label="Assessment">
                            <Typography variant="body2">{entry.assessment || "—"}</Typography>
                        </LabelBlock>
                    </Stack>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <LabelBlock label="Core element">
                                <Typography variant="body2">{entry.coreElement || "—"}</Typography>
                            </LabelBlock>
                            <LabelBlock label="Topic">
                                <Typography variant="body1" fontWeight={600}>
                                    {entry.topic || "—"}
                                </Typography>
                            </LabelBlock>
                            <LabelBlock label="Success criteria">
                                <BulletList text={entry.successCriteria} />
                            </LabelBlock>
                            <LabelBlock label="Planned activities">
                                <BulletList text={entry.plannedActivities} />
                            </LabelBlock>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <LabelBlock label="Teaching methods">
                                {methods.length ? (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {methods.map((m) => (
                                            <Chip key={m} label={m} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        —
                                    </Typography>
                                )}
                            </LabelBlock>
                            <LabelBlock label="Resources">
                                {resources.length ? (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {resources.map((r) => (
                                            <Chip key={r} label={r} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        —
                                    </Typography>
                                )}
                            </LabelBlock>
                        </Grid>
                    </Grid>
                )}
            </Box>

            {!isLegacyWeek && (
                <>
                    <Divider />
                    <Box sx={{ p: 2, bgcolor: "action.hover" }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 1 }}>
                            Record of work
                        </Typography>
                        {entry.isTaught ? (
                            <Stack spacing={1}>
                                <LabelBlock label="References">
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                        {entry.references || "—"}
                                    </Typography>
                                </LabelBlock>
                                <LabelBlock label="Outcomes">
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                        {entry.outcomes || "—"}
                                    </Typography>
                                </LabelBlock>
                                <LabelBlock label="Remarks">
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                        {entry.remarks || "—"}
                                    </Typography>
                                </LabelBlock>
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                Not yet marked as taught
                            </Typography>
                        )}
                    </Box>
                </>
            )}
        </Paper>
    );
}

const SchemeApprovals = () => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = currentUser?._id;
    const { recordsScope, setRecordsScope, currentTermLabel } = useTermRecordsScope(schoolId);

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState("");
    const [statusFilter, setStatusFilter] = useState("Submitted");
    const [search, setSearch] = useState("");

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [viewScheme, setViewScheme] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectTargetId, setRejectTargetId] = useState(null);
    const [rejectionNote, setRejectionNote] = useState("");
    const [actionLoading, setActionLoading] = useState(false);

    const loadSchemes = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get(`${getApiBaseUrl()}/Schemes/school/${schoolId}`, {
                params: buildTermRecordsQuery({ recordsScope }),
            });
            if (data?.message && !Array.isArray(data)) {
                setRows([]);
            } else {
                setRows(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Could not load schemes");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [schoolId, recordsScope]);

    useEffect(() => {
        loadSchemes();
    }, [loadSchemes]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (statusFilter !== "All" && (row.status || "Draft") !== statusFilter) return false;
            if (!q) return true;
            return (
                String(row.teacher?.name || "").toLowerCase().includes(q) ||
                String(row.subject?.subName || "").toLowerCase().includes(q) ||
                String(row.class?.sclassName || row.sclass?.sclassName || "").toLowerCase().includes(q) ||
                String(row.term || "").toLowerCase().includes(q) ||
                String(row.academicYear || "").toLowerCase().includes(q)
            );
        });
    }, [rows, statusFilter, search]);

    const summary = useMemo(() => {
        const total = rows.length;
        const pending = rows.filter((r) => r.status === "Submitted").length;
        const approved = rows.filter((r) => r.status === "Approved").length;
        const rejected = rows.filter((r) => r.status === "Rejected").length;
        return { total, pending, approved, rejected };
    }, [rows]);

    const notifySuccess = (text = "Completed successfully") => {
        setMessage(text);
        setShowPopup(true);
    };

    const openView = async (row) => {
        const id = row.id || row._id;
        setViewScheme(row);
        setDetailLoading(true);
        try {
            const { data } = await axios.get(`${getApiBaseUrl()}/SchemeOfWork/${id}`);
            if (data?.message && !data?.id && !data?._id) {
                setViewScheme(row);
            } else {
                setViewScheme(data);
            }
        } catch {
            setViewScheme(row);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleApprove = async (id) => {
        if (!window.confirm("Approve this scheme of work?")) return;
        setActionLoading(true);
        try {
            await axios.put(`${getApiBaseUrl()}/Scheme/${id}/approve`, {
                approvedById: schoolId,
            });
            notifySuccess("Scheme approved");
            await loadSchemes();
            setViewScheme(null);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not approve scheme");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    const openReject = (id) => {
        setRejectTargetId(id);
        setRejectionNote("");
        setRejectOpen(true);
    };

    const handleReject = async () => {
        if (!rejectTargetId) return;
        setActionLoading(true);
        try {
            await axios.put(`${getApiBaseUrl()}/Scheme/${rejectTargetId}/reject`, {
                rejectionNote: rejectionNote.trim(),
            });
            notifySuccess("Scheme rejected");
            setRejectOpen(false);
            setRejectTargetId(null);
            setViewScheme(null);
            await loadSchemes();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not reject scheme");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    const viewId = viewScheme?.id || viewScheme?._id;
    const viewStatus = viewScheme?.status || "Draft";
    const viewBlocks = schemeBlocks(viewScheme);

    return (
        <ResultsPageContainer>
            <ResultsPageHeader
                eyebrow="Teachers & HR"
                title="Scheme of Work Approvals"
                subtitle="Review teacher schemes aligned to the MIE format. Approve submitted plans or return them with feedback."
                action={
                    <Tooltip title="Refresh list">
                        <span>
                            <IconButton onClick={loadSchemes} disabled={loading} aria-label="Refresh schemes">
                                <RefreshOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                }
            />

            <TermRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
            <TermRecordsScopeAlert
                scope={recordsScope}
                currentTermLabel={currentTermLabel}
                sectionLabel="schemes of work"
            />

            {error ? (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            ) : null}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <KpiCard title="Total schemes" value={summary.total} icon={MenuBookOutlinedIcon} loading={loading} />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <KpiCard
                        title="Awaiting review"
                        value={summary.pending}
                        icon={PendingActionsOutlinedIcon}
                        color="warning"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <KpiCard
                        title="Approved"
                        value={summary.approved}
                        icon={TaskAltOutlinedIcon}
                        color="success"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <KpiCard
                        title="Rejected"
                        value={summary.rejected}
                        icon={CancelOutlinedIcon}
                        color="error"
                        loading={loading}
                    />
                </Grid>
            </Grid>

            <ResultsSectionPaper
                title="Submitted schemes"
                action={
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                        <TextField
                            size="small"
                            placeholder="Search teacher, subject, class…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlinedIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ minWidth: { xs: "100%", sm: 240 } }}
                        />
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Status</InputLabel>
                            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                {STATUS_OPTIONS.map((s) => (
                                    <MenuItem key={s} value={s}>
                                        {s}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                }
            >
                <ResponsiveTableWrap>
                    <Table size="small" sx={{ minWidth: 720 }}>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>Teacher</StyledTableCell>
                                <StyledTableCell>Subject</StyledTableCell>
                                <StyledTableCell>Class</StyledTableCell>
                                <StyledTableCell>Term</StyledTableCell>
                                <StyledTableCell>Year</StyledTableCell>
                                <StyledTableCell>Blocks</StyledTableCell>
                                <StyledTableCell>Progress</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(4)].map((_, i) => (
                                    <StyledTableRow key={i}>
                                        <StyledTableCell colSpan={9}>
                                            <Skeleton height={40} sx={{ borderRadius: 1 }} />
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            ) : filtered.length === 0 ? (
                                <EmptyResultsRow
                                    colSpan={9}
                                    message={
                                        statusFilter === "Submitted"
                                            ? "No schemes awaiting approval. Teachers submit from their Scheme of Work page."
                                            : "No schemes match this filter."
                                    }
                                />
                            ) : (
                                filtered.map((row) => {
                                    const id = row.id || row._id;
                                    const status = row.status || "Draft";
                                    const blocks = schemeBlocks(row);
                                    const progress = blockProgressValue(row);
                                    return (
                                        <StyledTableRow key={id} hover>
                                            <StyledTableCell>{row.teacher?.name || "—"}</StyledTableCell>
                                            <StyledTableCell>{row.subject?.subName || "—"}</StyledTableCell>
                                            <StyledTableCell>
                                                {row.class?.sclassName || row.sclass?.sclassName || "—"}
                                            </StyledTableCell>
                                            <StyledTableCell>{row.term || "—"}</StyledTableCell>
                                            <StyledTableCell>{row.academicYear || "—"}</StyledTableCell>
                                            <StyledTableCell>{blocks.length}</StyledTableCell>
                                            <StyledTableCell sx={{ minWidth: 120 }}>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {blockProgressLabel(row)}
                                                </Typography>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={progress}
                                                    sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    label={status}
                                                    size="small"
                                                    color={STATUS_CHIP[status] || "default"}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                                                    <Button
                                                        size="small"
                                                        startIcon={<VisibilityOutlinedIcon />}
                                                        onClick={() => openView(row)}
                                                    >
                                                        View
                                                    </Button>
                                                    {status === "Submitted" && (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                disabled={actionLoading}
                                                                onClick={() => handleApprove(id)}
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                disabled={actionLoading}
                                                                onClick={() => openReject(id)}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </>
                                                    )}
                                                </Stack>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </ResponsiveTableWrap>
            </ResultsSectionPaper>

            <Dialog open={Boolean(viewScheme)} onClose={() => setViewScheme(null)} fullScreen>
                <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1}
                    >
                        <Box>
                            <Typography variant="h6" fontWeight={800}>
                                Scheme of work
                            </Typography>
                            {viewScheme ? (
                                <Typography variant="body2" color="text.secondary">
                                    {viewScheme.teacher?.name} · {viewScheme.subject?.subName} ·{" "}
                                    {viewScheme.class?.sclassName || viewScheme.sclass?.sclassName} · {viewScheme.term}{" "}
                                    {viewScheme.academicYear}
                                    {viewScheme.periodsPerWeek
                                        ? ` · ${viewScheme.periodsPerWeek} periods/week`
                                        : ""}
                                </Typography>
                            ) : null}
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {viewScheme ? (
                                <Chip
                                    label={viewStatus}
                                    color={STATUS_CHIP[viewStatus] || "default"}
                                    size="small"
                                />
                            ) : null}
                            <Button onClick={() => setViewScheme(null)}>Close</Button>
                        </Stack>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: "background.default" }}>
                    {detailLoading ? (
                        <Stack spacing={2} sx={{ py: 2 }}>
                            {[1, 2, 3].map((i) => (
                                <Skeleton key={i} variant="rounded" height={160} />
                            ))}
                        </Stack>
                    ) : viewScheme ? (
                        <Stack spacing={2} sx={{ py: 1, maxWidth: 960, mx: "auto", width: "100%" }}>
                            {viewScheme.rejectionNote ? (
                                <Alert severity="error" variant="outlined">
                                    <strong>Rejection note:</strong> {viewScheme.rejectionNote}
                                </Alert>
                            ) : null}
                            {viewScheme.submittedAt ? (
                                <Typography variant="caption" color="text.secondary">
                                    Submitted {formatDate(viewScheme.submittedAt)}
                                    {viewScheme.approvedAt
                                        ? ` · Approved ${formatDate(viewScheme.approvedAt)}`
                                        : ""}
                                </Typography>
                            ) : null}
                            {viewBlocks.length === 0 ? (
                                <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
                                    <Typography color="text.secondary">
                                        This scheme has no content blocks yet.
                                    </Typography>
                                </Paper>
                            ) : (
                                viewBlocks.map((entry) => (
                                    <SchemeEntryReadOnly
                                        key={entry.id || entry._id || entry.weekNumber}
                                        entry={entry}
                                    />
                                ))
                            )}
                        </Stack>
                    ) : null}
                </DialogContent>
                {viewScheme && viewStatus === "Submitted" ? (
                    <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}>
                        <Button onClick={() => setViewScheme(null)} disabled={actionLoading}>
                            Close
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CancelOutlinedIcon />}
                            disabled={actionLoading}
                            onClick={() => openReject(viewId)}
                        >
                            Reject
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleOutlineIcon />}
                            disabled={actionLoading}
                            onClick={() => handleApprove(viewId)}
                        >
                            Approve scheme
                        </Button>
                    </DialogActions>
                ) : null}
            </Dialog>

            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject scheme</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The teacher will see your note and can revise the scheme before resubmitting.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="Rejection note (required)"
                        placeholder="Explain what needs to be changed…"
                        value={rejectionNote}
                        onChange={(e) => setRejectionNote(e.target.value)}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleReject}
                        disabled={actionLoading || !rejectionNote.trim()}
                    >
                        Reject scheme
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} showPopup={showPopup} setShowPopup={setShowPopup} />
        </ResultsPageContainer>
    );
};

export default SchemeApprovals;
